/**
 * EthicsGlobal Widget - Main Widget Class
 *
 * A professional, embeddable form widget that can be integrated
 * into any website without style conflicts.
 *
 * @example
 * // Basic usage
 * const widget = EthicsGlobal.init({
 *   container: '#my-form',
 *   domain: 'client.lineaetica.com',
 *   lang: 'es'
 * });
 *
 * // With all options
 * const widget = EthicsGlobal.init({
 *   container: '#form',
 *   domain: 'client.lineaetica.com',
 *   lang: 'es',
 *   theme: 'light',
 *   // Pre-select catalogues
 *   formParams: {
 *     bu: 'business_unit_key',  // Pre-select business unit
 *     rc: 'rc100_category_key'  // Pre-select RC-100 category
 *   },
 *   onReady: () => console.log('Form loaded'),
 *   onSubmit: (data) => console.log('Submitted:', data),
 *   onError: (error) => console.error(error)
 * });
 *
 * // Methods
 * widget.setLang('en');
 * widget.destroy();
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { MemoryRouter } from 'react-router-dom';
import { createWidgetStore } from './widget-store';
import { createWidgetApiClient } from './widget-api';
import { WidgetFormIsolated } from './WidgetFormIsolated';
import './widget-styles.scss';

// Version info
const VERSION = '2.0.0';

class EthicsGlobalWidget {
  /**
   * Create a new EthicsGlobal widget instance
   * @param {Object} options - Configuration options
   * @param {string|Element} options.container - Container element or selector
   * @param {string} options.domain - Tenant domain (required)
   * @param {string} [options.lang='es'] - Language code
   * @param {string} [options.theme='light'] - Theme: 'light' or 'dark'
   * @param {Object} [options.themeColors] - Custom theme colors
   * @param {string} [options.businessUnit] - Business unit (deprecated, use formParams.bu)
   * @param {Object} [options.formParams] - Pre-select catalogues { bu: 'key', rc: 'key' }
   * @param {string} [options.formIdentifier] - Specific form identifier
   * @param {Function} [options.onReady] - Called when form is ready
   * @param {Function} [options.onSubmit] - Called on form submission
   * @param {Function} [options.onError] - Called on error
   * @param {string} [options.apiUrl] - Custom API URL
   */
  constructor(options = {}) {
    this.options = {
      lang: 'es',
      theme: 'light',
      themeColors: null,
      businessUnit: null,
      formParams: {},
      formIdentifier: null,
      onReady: null,
      onSubmit: null,
      onError: null,
      apiUrl: null,
      ...options
    };

    // Merge businessUnit into formParams for backwards compatibility
    if (this.options.businessUnit && !this.options.formParams.bu) {
      this.options.formParams.bu = this.options.businessUnit;
    }

    // Validate required options
    if (!options.container) {
      throw new Error('[EthicsGlobal] Container is required');
    }
    if (!options.domain) {
      throw new Error('[EthicsGlobal] Domain is required');
    }

    // Get container element
    this.container = typeof options.container === 'string'
      ? document.querySelector(options.container)
      : options.container;

    if (!this.container) {
      throw new Error(`[EthicsGlobal] Container "${options.container}" not found`);
    }

    // Instance state
    this.root = null;
    this.store = null;
    this.apiClient = null;
    this.isRendered = false;
    this.instanceId = `eg-widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Event emitter
    this._events = {};

    // Auto-render
    this.render();
  }

  /**
   * Render the widget
   */
  render() {
    if (this.isRendered) {
      console.warn('[EthicsGlobal] Widget is already rendered');
      return this;
    }

    try {
      // Create isolated store for this widget instance
      this.store = createWidgetStore();

      // Create API client
      this.apiClient = createWidgetApiClient(
        this.options.domain,
        this.options.apiUrl || undefined
      );

      // Apply custom theme colors if provided
      if (this.options.themeColors) {
        this._applyThemeColors(this.options.themeColors);
      }

      // Create React root
      this.root = ReactDOM.createRoot(this.container);

      // Create widget component
      // MemoryRouter is used to provide router context without interfering with host page
      const WidgetApp = () => (
        <Provider store={this.store}>
          <MemoryRouter>
            <WidgetFormIsolated
              domain={this.options.domain}
              lang={this.options.lang}
              theme={this.options.theme}
              businessUnit={this.options.businessUnit}
              formParams={this.options.formParams}
              formIdentifierName={this.options.formIdentifier}
              apiClient={this.apiClient}
              onReady={this._handleReady.bind(this)}
              onSubmit={this._handleSubmit.bind(this)}
              onError={this._handleError.bind(this)}
            />
          </MemoryRouter>
        </Provider>
      );

      this.root.render(<WidgetApp />);
      this.isRendered = true;

      // Add instance ID to container
      this.container.setAttribute('data-eg-widget-id', this.instanceId);

      return this;
    } catch (error) {
      console.error('[EthicsGlobal] Error rendering widget:', error);
      this._handleError(error);
      return this;
    }
  }

  /**
   * Destroy the widget and clean up
   */
  destroy() {
    if (this.root && this.isRendered) {
      this.root.unmount();
      this.root = null;
      this.isRendered = false;
      this.container.removeAttribute('data-eg-widget-id');

      // Remove custom theme styles
      const styleEl = document.getElementById(`${this.instanceId}-styles`);
      if (styleEl) {
        styleEl.remove();
      }

      this._emit('destroy');
    }
    return this;
  }

  /**
   * Set the language
   * @param {string} lang - Language code
   */
  setLang(lang) {
    this.options.lang = lang;
    if (this.isRendered) {
      // Re-render with new language
      this.destroy();
      this.render();
    }
    return this;
  }

  /**
   * Set the theme
   * @param {string} theme - 'light' or 'dark'
   */
  setTheme(theme) {
    this.options.theme = theme;
    if (this.isRendered) {
      this.destroy();
      this.render();
    }
    return this;
  }

  /**
   * Update options and re-render
   * @param {Object} newOptions - Options to update
   */
  updateOptions(newOptions) {
    this.options = { ...this.options, ...newOptions };
    if (this.isRendered) {
      this.destroy();
      this.render();
    }
    return this;
  }

  /**
   * Add event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this._events[event]) {
      this._events[event] = [];
    }
    this._events[event].push(callback);
    return this;
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    if (this._events[event]) {
      this._events[event] = this._events[event].filter(cb => cb !== callback);
    }
    return this;
  }

  // Private methods

  _emit(event, data) {
    if (this._events[event]) {
      this._events[event].forEach(callback => callback(data));
    }
  }

  _handleReady(data) {
    if (this.options.onReady) {
      this.options.onReady(data);
    }
    this._emit('ready', data);
  }

  _handleSubmit(data) {
    if (this.options.onSubmit) {
      this.options.onSubmit(data);
    }
    this._emit('submit', data);
  }

  _handleError(error) {
    if (this.options.onError) {
      this.options.onError(error);
    }
    this._emit('error', error);
  }

  _applyThemeColors(colors) {
    const styleId = `${this.instanceId}-styles`;

    // Remove existing style element if any
    const existing = document.getElementById(styleId);
    if (existing) {
      existing.remove();
    }

    // Create CSS variables
    const cssVars = [];
    if (colors.primaryColor) {
      cssVars.push(`--eg-primary-color: ${colors.primaryColor}`);
    }
    if (colors.secondaryColor) {
      cssVars.push(`--eg-secondary-color: ${colors.secondaryColor}`);
    }
    if (colors.textColor) {
      cssVars.push(`--eg-text-color: ${colors.textColor}`);
    }
    if (colors.backgroundColor) {
      cssVars.push(`--eg-background-color: ${colors.backgroundColor}`);
    }
    if (colors.errorColor) {
      cssVars.push(`--eg-error-color: ${colors.errorColor}`);
    }
    if (colors.successColor) {
      cssVars.push(`--eg-success-color: ${colors.successColor}`);
    }
    if (colors.fontFamily) {
      cssVars.push(`--eg-font-family: ${colors.fontFamily}`);
    }

    if (cssVars.length > 0) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        [data-eg-widget-id="${this.instanceId}"] .eg-widget {
          ${cssVars.join(';\n          ')};
        }
      `;
      document.head.appendChild(style);
    }
  }

  // Static methods

  /**
   * Create and initialize a new widget
   * @param {Object} options - Configuration options
   * @returns {EthicsGlobalWidget}
   */
  static init(options) {
    return new EthicsGlobalWidget(options);
  }

  /**
   * Get the widget version
   * @returns {string}
   */
  static get version() {
    return VERSION;
  }
}

// Export for module usage
export { EthicsGlobalWidget };
export default EthicsGlobalWidget;

// Global API object
const EthicsGlobal = {
  /**
   * Initialize a new widget
   * @param {Object} options - Configuration options
   * @returns {EthicsGlobalWidget}
   */
  init(options) {
    return EthicsGlobalWidget.init(options);
  },

  /**
   * Widget version
   */
  version: VERSION,

  /**
   * Widget class for advanced usage
   */
  Widget: EthicsGlobalWidget
};

// Expose to window for script tag usage
if (typeof window !== 'undefined') {
  window.EthicsGlobal = EthicsGlobal;
  window.EthicsGlobalWidget = EthicsGlobalWidget;

  // Process queued commands if using async loader
  if (window.EthicsGlobal && window.EthicsGlobal.q) {
    const queue = window.EthicsGlobal.q;
    window.EthicsGlobal = EthicsGlobal;
    queue.forEach(args => {
      if (args[0] === 'init') {
        EthicsGlobal.init(args[1]);
      }
    });
  }
}

export { EthicsGlobal };
